/**
 * Progressive pagination with an optional hybrid mode. Product grids can load
 * a small number of batches automatically, then leave an explicit Load more
 * control so the footer remains reachable.
 */
if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.bfcacheHandler = this.reload.bind(this);
      document.addEventListener('on:bfcache:load-restore', this.bfcacheHandler);
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
      document.removeEventListener('on:bfcache:load-restore', this.bfcacheHandler);
    }

    init() {
      this.generation = (this.generation || 0) + 1;
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.hybridPagination = this.dataset.hybridPagination === 'true';
      this.autoLoadLimit = Number.parseInt(this.dataset.autoLoadLimit || '2', 10);
      this.autoLoads = Number.parseInt(this.dataset.autoLoads || '0', 10);
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      this.results = this.querySelectorAll('.js-pagination-result');
      this.loadedPage = Number.parseInt(this.pagination?.dataset.currentPage || '1', 10);
      this.controller = null;
      this.isFetching = false;
      this.stateKey = this.getStateKey();

      this.liveRegion = this.querySelector('.js-pagination-status')
        || this.querySelector(':scope > .js-pagination-live-region');
      if (!this.liveRegion) {
        this.liveRegion = document.createElement('span');
        this.liveRegion.className = 'visually-hidden js-pagination-live-region';
        this.liveRegion.setAttribute('role', 'status');
        this.liveRegion.setAttribute('aria-live', 'polite');
        this.append(this.liveRegion);
      }

      this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
      this.loadMoreClickHandler = this.loadMoreClickHandler || this.handleLoadMoreClick.bind(this);
      this.addEventListener('click', this.resultClickHandler);
      this.bindLoadMoreButton();

      const savedState = history.state?.wfsPagination;
      if (savedState?.key === this.stateKey) {
        this.autoLoads = Math.max(this.autoLoads, savedState.autoLoads || 0);
        this.dataset.autoLoads = this.autoLoads;

        if (savedState.loadedPage > this.loadedPage) {
          this.restorePages(savedState, this.generation);
          return;
        }
      }

      this.setupPaginationObserver();
    }

    destroy() {
      this.generation = (this.generation || 0) + 1;
      if (this.paginationObserver) this.paginationObserver.disconnect();
      const controller = this.controller;
      this.controller = null;
      this.isFetching = false;
      if (controller) controller.abort();
      if (this.loadMoreButton) this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      this.removeEventListener('click', this.resultClickHandler);
    }

    reload() {
      this.destroy();
      this.init();
    }

    getStateKey() {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      return `${url.pathname}?${url.searchParams.toString()}`;
    }

    hasMoreResults() {
      return this.pagination?.dataset.isMoreResults === 'true' && Boolean(this.loadMoreButton);
    }

    shouldAutoLoad() {
      if (!this.infiniteScroll || !this.hasMoreResults()) return false;
      return !this.hybridPagination || this.autoLoads < this.autoLoadLimit;
    }

    bindLoadMoreButton() {
      if (!this.loadMoreButton) return;
      this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
    }

    setupPaginationObserver(generation = this.generation) {
      if (generation !== this.generation || !this.shouldAutoLoad()) return;
      if (this.paginationObserver) this.paginationObserver.disconnect();

      const observer = new IntersectionObserver(
        async (entries) => {
          if (
            generation !== this.generation
            || !entries.some((entry) => entry.isIntersecting)
            || this.isFetching
          ) return;

          observer.disconnect();
          const loaded = await this.loadMore({ automatic: true, generation });
          if (generation === this.generation && loaded && this.shouldAutoLoad()) {
            requestAnimationFrame(() => this.setupPaginationObserver(generation));
          }
        },
        { rootMargin: this.hybridPagination ? '400px 0px' : `${window.innerHeight}px 0px` }
      );

      this.paginationObserver = observer;
      observer.observe(this.pagination);
    }

    handleLoadMoreClick(evt) {
      evt.preventDefault();
      this.loadMore({ automatic: false });
    }

    handleResultClick(evt) {
      if (!evt.target.closest('a') || !evt.target.closest('.js-pagination-result')) return;
      if (this.controller) this.controller.abort();
      this.persistState(window.scrollY);
    }

    persistState(scrollY = window.scrollY) {
      const state = {
        ...(history.state || {}),
        wfsPagination: {
          key: this.stateKey,
          loadedPage: this.loadedPage,
          autoLoads: this.autoLoads,
          scrollY
        }
      };
      history.replaceState(state, '', window.location.href);
    }

    async restorePages(savedState, generation = this.generation) {
      while (
        generation === this.generation
        && this.loadedPage < savedState.loadedPage
        && this.hasMoreResults()
      ) {
        const loaded = await this.loadMore({ restoring: true, generation });
        if (!loaded) break;
      }

      if (generation !== this.generation) return;

      this.autoLoads = savedState.autoLoads || 0;
      this.dataset.autoLoads = this.autoLoads;
      this.setupPaginationObserver(generation);
      requestAnimationFrame(() => {
        if (generation !== this.generation) return;
        window.scrollTo(0, savedState.scrollY || 0);
        this.persistState(savedState.scrollY || 0);
      });
    }

    setLoadingState(isLoading, button = this.loadMoreButton, pagination = this.pagination) {
      if (!button) return;
      const canReplaceLabel = pagination?.dataset.paginationStyle === 'modern';

      if (isLoading) {
        button.classList.add('is-loading');
        button.setAttribute('aria-disabled', 'true');
        if (canReplaceLabel) {
          button.dataset.previousLabel = button.textContent.trim();
          button.textContent = 'Loading…';
        }
      } else {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
        if (canReplaceLabel) {
          button.textContent = button.dataset.defaultLabel
            || button.dataset.previousLabel
            || 'Load more';
        }
      }
    }

    updatePagination(nextPagination, { automatic, addedCount }) {
      const hasMore = nextPagination?.dataset.isMoreResults === 'true';
      this.loadedPage = Number.parseInt(nextPagination?.dataset.currentPage || `${this.loadedPage + 1}`, 10);

      ['isMoreResults', 'currentPage', 'visibleResults', 'totalResults'].forEach((key) => {
        if (nextPagination?.dataset[key] !== undefined) {
          this.pagination.dataset[key] = nextPagination.dataset[key];
        }
      });

      const nextMessage = nextPagination?.querySelector('.js-pagination-message');
      const currentMessage = this.pagination.querySelector('.js-pagination-message');
      if (nextMessage && currentMessage) currentMessage.innerHTML = nextMessage.innerHTML;

      const nextBar = nextPagination?.querySelector('.js-pagination-bar');
      const currentBar = this.pagination.querySelector('.js-pagination-bar');
      if (nextBar && currentBar) currentBar.setAttribute('style', nextBar.getAttribute('style') || '');

      if (automatic) {
        this.autoLoads += 1;
        this.dataset.autoLoads = this.autoLoads;
      }

      if (this.pagination.dataset.paginationStyle === 'traditional') {
        this.pagination.innerHTML = nextPagination.innerHTML;
        this.loadMoreButton = this.pagination.querySelector('.js-pagination-load-more');
        this.bindLoadMoreButton();
      } else if (hasMore) {
        const nextButton = nextPagination.querySelector('.js-pagination-load-more');
        this.loadMoreButton.href = nextButton.href;
        this.loadMoreButton.dataset.defaultLabel = nextButton.dataset.defaultLabel || 'Load more';
        this.setLoadingState(false);
      } else {
        this.pagination.dataset.isMoreResults = 'false';
        this.loadMoreButton.remove();
        this.loadMoreButton = null;
      }

      if (!hasMore) {
        document.querySelectorAll('.js-when-paginated-only').forEach((element) => element.remove());
      }

      const visibleResults = this.pagination.dataset.visibleResults || this.results.length;
      const totalResults = this.pagination.dataset.totalResults || visibleResults;
      const resultLabel = this.hybridPagination ? 'products' : 'items';
      this.liveRegion.textContent = `${addedCount} more ${resultLabel} loaded. ${visibleResults} of ${totalResults} shown.`;
      this.persistState();
    }

    async loadMore({ automatic = false, restoring = false, generation = this.generation } = {}) {
      if (
        generation !== this.generation
        || this.isFetching
        || this.dataset.pauseInfiniteScroll === 'true'
        || !this.hasMoreResults()
      ) {
        return false;
      }

      this.isFetching = true;
      const controller = new AbortController();
      const loadMoreButton = this.loadMoreButton;
      const pagination = this.pagination;
      this.controller = controller;
      const requestUrl = loadMoreButton.href;
      this.setLoadingState(true, loadMoreButton, pagination);

      try {
        const response = await fetch(requestUrl, { signal: controller.signal });
        if (!response.ok) throw new Error(response.status);
        if (generation !== this.generation || this.controller !== controller) return false;
        if (this.dataset.pauseInfiniteScroll === 'true') throw new Error('Pagination was interrupted.');

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        if (generation !== this.generation || this.controller !== controller) return false;

        const nextComponent = tmpl.content.querySelector('custom-pagination.main-products-grid')
          || tmpl.content.querySelector('custom-pagination');
        if (!nextComponent) throw new Error('Pagination response missing.');

        const newResults = [...nextComponent.querySelectorAll('.js-pagination-result')];
        const lastResult = this.results[this.results.length - 1];
        if (!lastResult || newResults.length === 0) throw new Error('No additional results found.');

        const fragment = document.createDocumentFragment();
        newResults.forEach((result) => fragment.append(result.cloneNode(true)));
        lastResult.after(fragment);
        if (window.initLazyImages) window.initLazyImages();
        this.results = this.querySelectorAll('.js-pagination-result');

        const nextPagination = nextComponent.querySelector('.js-pagination');
        if (!nextPagination) throw new Error('Next pagination controls missing.');
        this.updatePagination(nextPagination, { automatic, addedCount: newResults.length });

        if (!restoring && !automatic && this.loadMoreButton) {
          this.loadMoreButton.focus();
        } else if (!restoring && !automatic) {
          this.pagination.setAttribute('tabindex', '-1');
          this.pagination.focus();
        }
        if (!restoring && !automatic && this.shouldAutoLoad()) this.setupPaginationObserver();
        return true;
      } catch (error) {
        if (generation !== this.generation || this.controller !== controller) return false;
        if (error.name === 'AbortError') {
          this.setLoadingState(false, loadMoreButton, pagination);
          return false;
        }

        if (this.loadMoreButton) {
          this.setLoadingState(false);
          if (this.pagination?.dataset.paginationStyle === 'modern') {
            this.loadMoreButton.textContent = 'Try again';
          }
        }
        this.liveRegion.textContent = 'Could not load more products. Use Try again to retry.';
        return false;
      } finally {
        if (generation === this.generation && this.controller === controller) {
          this.controller = null;
          this.isFetching = false;
        }
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
